import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Loader2, Palette, Save, RotateCcw, ArrowLeft, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const BrandingTab = () => {
  const [tenants, setTenants] = useState([]);
  const confirm = useConfirm();
  const [selectedTenant, setSelectedTenant] = useState('');
  const [brandingData, setBrandingData] = useState({
    primary_color: '#1e5128',
    secondary_color: '#ffffff',
    white_label_enabled: false,
    logo_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoBlob, setLogoBlob] = useState(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      fetchTenantBranding();
    }
  }, [selectedTenant]);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantBranding = async () => {
    try {
      const response = await api.get(`/tenants/${selectedTenant}`);
      const tenant = response.data;
      setBrandingData({
        primary_color: tenant.settings?.primary_color || '#1e5128',
        secondary_color: tenant.settings?.secondary_color || '#ffffff',
        white_label_enabled: tenant.settings?.hide_powered_by || false,
        logo_url: tenant.logo_url || ''
      });
      
      // Fetch logo with authentication if exists
      if (tenant.logo_url) {
        await fetchLogoBlob(tenant.logo_url);
      } else {
        setLogoPreview('');
        setLogoBlob(null);
      }
    } catch (error) {
      console.error('Error fetching tenant branding:', error);
    }
  };

  const fetchLogoBlob = async (logoUrl) => {
    try {
      const token = localStorage.getItem('token');
      // Build full URL — handles both relative /api/files/public/... and legacy private paths
      const fullUrl = logoUrl.startsWith('http')
        ? logoUrl
        : `${window.location.origin}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
      const response = await fetch(fullUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setLogoBlob(blobUrl);
        setLogoPreview(blobUrl);
      } else {
        setLogoBlob(null);
        setLogoPreview('');
      }
    } catch (error) {
      console.error('Error loading logo:', error);
      setLogoBlob(null);
      setLogoPreview('');
    }
  };

  const handleLogoSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!selectedTenant) {
      toast.error('Please select a tenant');
      return;
    }

    setSaving(true);
    try {
      // Upload logo if selected
      let logoUrl = brandingData.logo_url;
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        formData.append('entity_type', 'tenant');
        formData.append('entity_id', selectedTenant);
        formData.append('description', 'Tenant Logo');
        formData.append('category', 'tenant_logo');

        const uploadResponse = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Always use the public endpoint so owner portal can display without auth
        const fileId = uploadResponse.data.id;
        logoUrl = `/api/files/public/${fileId}`;
      }
      
      // Update tenant settings with branding info
      await api.put(`/tenants/${selectedTenant}`, {
        logo_url: logoUrl,
        settings: {
          primary_color: brandingData.primary_color,
          secondary_color: brandingData.secondary_color,
          hide_powered_by: brandingData.white_label_enabled,
          custom_branding_enabled: true
        }
      });
      
      toast.success('Branding settings saved');
      setLogoFile(null);
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToStandard = async () => {
    if (!await confirm({ title: 'Revert to standard Green Door branding?', destructive: true })) return;

    setSaving(true);
    try {
      await api.put(`/tenants/${selectedTenant}`, {
        settings: {
          primary_color: '#1e5128',
          secondary_color: '#ffffff',
          hide_powered_by: false,
          custom_branding_enabled: false
        }
      });
      
      setBrandingData({
        primary_color: '#1e5128',
        secondary_color: '#ffffff',
        white_label_enabled: false
      });
      
      toast.success('Reverted to standard branding');
    } catch (error) {
      console.error('Error reverting branding:', error);
      toast.error('Failed to revert branding');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    setSelectedTenant('');
    setLogoFile(null);
    setLogoPreview('');
    setBrandingData({
      primary_color: '#1e5128',
      secondary_color: '#ffffff',
      white_label_enabled: false,
      logo_url: ''
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tenant Branding Control</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tenant Selection */}
          <div className="space-y-2">
            <Label>Select Tenant</Label>
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a tenant..." />
              </SelectTrigger>
              <SelectContent>
                {tenants.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTenant && (
            <>
              {/* Primary Color */}
              <div className="space-y-2">
                <Label>Primary Brand Color</Label>
                <div className="flex gap-4 items-center">
                  <Input
                    type="color"
                    value={brandingData.primary_color}
                    onChange={(e) => setBrandingData({ ...brandingData, primary_color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={brandingData.primary_color}
                    onChange={(e) => setBrandingData({ ...brandingData, primary_color: e.target.value })}
                    placeholder="#1e5128"
                    className="flex-1"
                  />
                  <div 
                    className="w-20 h-10 rounded border"
                    style={{ backgroundColor: brandingData.primary_color }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Used for buttons, links, and primary UI elements</p>
              </div>

              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Tenant Logo</Label>
                <div className="flex gap-4 items-start">
                  {/* Preview */}
                  <div className="w-32 h-32 border-2 border-dashed rounded flex items-center justify-center bg-muted/20">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain p-2" />
                    ) : (
                      <p className="text-xs text-muted-foreground text-center">No logo</p>
                    )}
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('tenant-logo-input').click()}
                      >
                        Choose Logo
                      </Button>
                      {logoPreview && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setLogoFile(null);
                            setLogoPreview('');
                            setBrandingData({ ...brandingData, logo_url: '' });
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Upload a logo to replace Green Door branding in the app header.
                      Recommended: PNG with transparent background, 200x50px
                    </p>
                  </div>
                  <input
                    id="tenant-logo-input"
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoSelect}
                    className="hidden"
                  />
                </div>
              </div>


              {/* White Label Option */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>White Label Mode</Label>
                    <p className="text-xs text-muted-foreground">Remove &quot;Powered by Green Door&quot; branding</p>
                  </div>
                  <Switch
                    checked={brandingData.white_label_enabled}
                    onCheckedChange={(checked) => setBrandingData({ ...brandingData, white_label_enabled: checked })}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t flex justify-between items-center">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleBackToList}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Tenant List
                  </Button>
                  <Button variant="outline" onClick={handleRevertToStandard} disabled={saving}>
                    <RotateCcw className="w-4 h-4 mr-2" /> Revert to Standard
                  </Button>
                </div>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save Branding Settings</>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandingTab;
